import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_API_URL } from "../utils/constants";

const AdminAnalyticsSlice = createSlice({
  name: "AdminAnalytics",
  initialState: {
    loader: false,
    error: null,
    monthView: [],
    monthdata: [],
    totalRows: 0,
    usersMap: {},
    topPages: [],
    topVideos: [],
    downloadLoading: false,
  },
  reducers: {
    setLoader: (state, action) => {
      state.loader = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setMonthView: (state, action) => {
      state.monthView = action.payload || [];
    },
    setMonthDetails: (state, action) => {
      state.monthdata = action.payload?.monthdata || [];
      state.totalRows = action.payload?.totalRows || 0;
    },
    setUsersMap: (state, action) => {
      state.usersMap = action.payload || {};
    },
    setTopPages: (state, action) => {
      state.topPages = action.payload || [];
    },
    setTopVideos: (state, action) => {
      state.topVideos = action.payload || [];
    },
    setDownloadLoading: (state, action) => {
      state.downloadLoading = action.payload;
    },
    resetAdminAnalyticsError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setLoader,
  setError,
  setMonthView,
  setMonthDetails,
  setUsersMap,
  setTopPages,
  setTopVideos,
  setDownloadLoading,
  resetAdminAnalyticsError,
} = AdminAnalyticsSlice.actions;

export default AdminAnalyticsSlice.reducer;

export function fetchYearAnalytics(selectedYear) {
  return async (dispatch) => {
    dispatch(setLoader(true));
    dispatch(setError(null));

    try {
      const response = await axios.post(
        `${BASE_API_URL}/yearanalytics`,
        { year: selectedYear },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        },
      );

      dispatch(setMonthView(response.data));
    } catch (error) {
      dispatch(setError(true));
    } finally {
      dispatch(setLoader(false));
    }
  };
}

export function fetchDetailsPerMonth(
  selectedMonth,
  selectedYear,
  page,
  rowsPerPage,
) {
  return async (dispatch) => {
    dispatch(setLoader(true));
    dispatch(setError(null));

    try {
      const response = await axios.post(
        `${BASE_API_URL}/fetchDetailsPerMonth`,
        {
          month: selectedMonth,
          year: selectedYear,
          page,
          rowsPerPage,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        },
      );

      dispatch(
        setMonthDetails({
          monthdata: response.data?.[0]?.Data || [],
          totalRows: response.data?.[0]?.totalRows || 0,
        }),
      );
    } catch (error) {
      dispatch(setError(true));
    } finally {
      dispatch(setLoader(false));
    }
  };
}

export function fetchTopAnalyticsData(selectedYear) {
  return async (dispatch) => {
    dispatch(setLoader(true));
    dispatch(setError(null));

    try {
      const [pagesRes, videosRes] = await Promise.all([
        axios.get(`${BASE_API_URL}/top-pages?year=${selectedYear}`, {
          headers: { Authorization: localStorage.getItem("token") },
        }),
        axios.get(`${BASE_API_URL}/top-videos?year=${selectedYear}`, {
          headers: { Authorization: localStorage.getItem("token") },
        }),
      ]);

      dispatch(setTopPages(pagesRes.data || []));
      dispatch(setTopVideos(videosRes.data || []));
    } catch (err) {
      dispatch(setTopPages([]));
      dispatch(setTopVideos([]));
      dispatch(setError(true));
    } finally {
      dispatch(setLoader(false));
    }
  };
}

export function fetchAnalyticsUsers() {
  return async (dispatch) => {
    try {
      const response = await axios.get(`${BASE_API_URL}/getUsers`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      });

      const map = {};
      (response.data || []).forEach((user) => {
        map[user.enterpriseId] = user;
      });

      dispatch(setUsersMap(map));
    } catch (error) {
      console.error(error);
    }
  };
}

export function downloadAnalyticsFile(selectedYear) {
  return async (dispatch) => {
    try {
      dispatch(setDownloadLoading(true));

      const response = await axios.get(
        `${BASE_API_URL}/downloadAnalytics/${selectedYear}`,
        {
          responseType: "blob",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        },
      );

      const fileURL = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `AnalyticsData_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileURL);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      dispatch(setDownloadLoading(false));
    }
  };
}
